'use client'

import { cn } from '@repo/ui/lib'
import * as React from 'react'
import { DayFlag, DayPicker, SelectionState, UI } from 'react-day-picker'

import { buttonVariants } from './button.tsx'

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      className={cn('relative p-3', className)}
      classNames={{
        caption_label: 'text-sm font-medium',
        day: cn(
          buttonVariants({ variant: 'ghost' }),
          'text-black aria-selected:hover:text-white',
          'h-9 w-10 p-0 font-normal aria-selected:opacity-100',
          'h-9 w-10 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-slate-100/50 [&:has([aria-selected])]:bg-slate-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 dark:[&:has([aria-selected].day-outside)]:bg-slate-800/50 dark:[&:has([aria-selected])]:bg-slate-800',
        ),
        day_button:
          'dark:text-slate-300 flex items-center justify-center w-full h-full cursor-pointer',
        [DayFlag.disabled]: 'text-slate-500 opacity-50 dark:text-slate-400',
        [DayFlag.hidden]: 'invisible',
        [DayFlag.outside]:
          'day-outside text-slate-500 opacity-50 aria-selected:bg-slate-100/50 aria-selected:text-slate-500 aria-selected:opacity-30 dark:text-slate-400 dark:aria-selected:bg-slate-800/50 dark:aria-selected:text-slate-400',
        [DayFlag.today]: 'bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50',
        month: 'space-y-4',
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-y-0 relative',
        [SelectionState.range_end]: 'day-range-end',
        [SelectionState.range_middle]:
          'aria-selected:bg-slate-100 aria-selected:text-slate-900 dark:aria-selected:bg-slate-800 dark:aria-selected:text-slate-50',
        [SelectionState.selected]:
          '!bg-gray-700 text-white hover:bg-slate-900 hover:text-white focus:bg-slate-900 focus:text-slate-50 dark:bg-slate-50 dark:text-slate-900 dark:hover:bg-slate-50 dark:hover:text-slate-900 dark:focus:bg-slate-50 dark:focus:text-slate-900',
        [UI.MonthCaption]: 'flex justify-center pt-1 relative items-center',
        [UI.MonthGrid]: 'w-full border-collapse space-y-1',
        [UI.Nav]: 'flex absolute top-0 w-full justify-between z-50 px-1',
        [UI.NextMonthButton]: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        [UI.PreviousMonthButton]: cn(
          buttonVariants({ variant: 'outline' }),
          'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
        ),
        [UI.Week]: 'flex justify-center w-full mt-2',
        [UI.Weekday]:
          'text-slate-500 rounded-md w-10 font-normal text-[0.8rem] dark:text-slate-400',
        [UI.Weekdays]: 'flex justify-center',
        ...classNames,
      }}
      showOutsideDays={showOutsideDays}
      weekStartsOn={1}
      {...props}
    />
  )
}
Calendar.displayName = 'Calendar'

export { Calendar }
